import type { UsageFilterKey, UsageRange, UsageTotals } from '@shared/common-types.js'

export type UsageMetric = keyof UsageTotals

export const METRICS: { key: UsageMetric; label: string; title?: string }[] = [
  { key: 'users', label: '日活', title: '当天打开过 App 的人（有 session_start），不含后台唤醒' },
  { key: 'sessions', label: '会话' },
  { key: 'events', label: '事件' },
  { key: 'avgDurationSeconds', label: '平均时长' },
  {
    key: 'backgroundUsers',
    label: '后台唤醒',
    title: '被 iOS 在后台拉起过的设备（app_started 之后 60 秒内没有前台事件）。掉了说明后台定位在退化',
  },
]

export const RANGE_OPTIONS: { value: UsageRange; label: string; current: string; previous: string }[] = [
  { value: 'today', label: '今天', current: '今天', previous: '昨天' },
  { value: 'yesterday', label: '昨天', current: '昨天', previous: '前天' },
  { value: '7d', label: '最近 7 天', current: '最近 7 天', previous: '前 7 天' },
  { value: '30d', label: '最近 30 天', current: '最近 30 天', previous: '前 30 天' },
]

export const FILTER_LABELS: Record<UsageFilterKey, string> = {
  country: '国家',
  osVersion: 'iOS',
  appVersion: '版本',
  event: '事件',
}

/** 没带这个属性的那一行 */
export const UNKNOWN_LABEL = '（未知）'

const numberFormat = new Intl.NumberFormat('zh-CN')

export function formatMetric(metric: UsageMetric, value: number | null): string {
  if (value === null) return '—'
  if (metric !== 'avgDurationSeconds') return numberFormat.format(value)
  const seconds = Math.round(value)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** 纵轴刻度：要放得进手机上 44px 宽的轴，所以千位缩成 k（一万以上取整）、时长只到分钟 */
export function formatAxisTick(metric: UsageMetric, value: number): string {
  if (metric === 'avgDurationSeconds') return value >= 60 ? `${Math.round((value / 60) * 10) / 10}m` : `${value}s`
  if (value >= 10_000) return `${Math.round(value / 1000)}k`
  if (value >= 1000) return `${Math.round((value / 1000) * 10) / 10}k`
  return String(value)
}

/** 涨跌百分比；对比时段是 0 或没有数据时没法算 */
export function changeRatio(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null
  return (current - previous) / previous
}

const regionNames = new Intl.DisplayNames(['zh-CN'], { type: 'region' })

/** 国家码 → 中文国家名，认不出的码用 PostHog 给的英文名 */
export function countryName(code: string, fallback: string): string {
  if (!code) return UNKNOWN_LABEL
  try {
    return regionNames.of(code) ?? fallback
  } catch {
    return fallback || code
  }
}

/** 国家码 → 国旗 emoji（两个区域指示符） */
export function countryFlag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '🏳️'
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)))
}

const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000

/** 第 i 个桶的横轴标签：按小时是「9时」，按天是「10-25」（北京时间） */
export function bucketLabel(startIso: string, granularity: 'hour' | 'day', index: number): string {
  if (granularity === 'hour') return `${index}时`
  const date = new Date(Date.parse(startIso) + SHANGHAI_OFFSET_MS + index * 24 * 60 * 60 * 1000)
  return `${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}
